import BaseService from '../../../base/base_service';
import _ from '../../../utils/underscore';
import IUserService, {
  UserServiceInitParams,
  ITokenResponse,
  IValidateGeolocationResponse, geoRetryResponse
} from './user_service_interface';
import {ICashierWidgetsEligibilityDetails} from "../../../../../common/interfaces/cashier-widgets-eligibility-details.interface";
import { SYSTEM_EVENTS } from '../../../events/events';
import {Check} from "@sparkware/app-utils";

class UserService extends BaseService implements IUserService{
  static CLASS: string = 'UserService';

  private _initParams: UserServiceInitParams = null;
  private _firebaseLoginToken: string = '';

  protected init(params: UserServiceInitParams): void {
    super.init(params);
    this._initParams = {
      tokenUrl: params.tokenUrl ? params.tokenUrl : '/cgpapi/user/gettoken',
      realityCheckDataUrl: params.realityCheckDataUrl,
      validateGeoLocationUrl: params.validateGeoLocationUrl,
      initRetryGeoUrl: params.initRetryGeoUrl,
      updateRealityCheckDataUrl: params.updateRealityCheckDataUrl
    };

    this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, () => {
      if (this.serviceFactory.oAuth2Service().isAuthorized()) {
        this._onLogin();
      }
    });
  }

  private _onLogin() {
    const regulation = this.serviceFactory.regulationService().getRegulation();
    if (regulation.attemptRealityCheckIntervalMigrationFromLocalStorageToControlCenter) {
      regulation.attemptRealityCheckIntervalMigrationFromLocalStorageToControlCenter();
    }
  }

  private _oAuth2Service(): any { // need interface for OAuth2Service
    return this.serviceFactory.oAuth2Service();
  }

  public validateGeoLocation(geoLocationEncryptedResponse: string, isPeriodicFlow: boolean, errorCode: number =-1, errorMsg: string =''): Promise<IValidateGeolocationResponse> {
    const requestParams = {
      geoLocationEncryptedResponse: geoLocationEncryptedResponse,
      isPeriodicFlow: isPeriodicFlow.toString(),
      errorCode: errorCode.toString(),
      errorMsg: errorMsg
    };
    return this.serviceFactory.http2Service().post(this._initParams.validateGeoLocationUrl, requestParams);
  }

  public isGeoRetryEnable(): boolean {
    return !Check.isEmpty(this._initParams.initRetryGeoUrl);
  }

  public initGeoRetry(): Promise<geoRetryResponse> {
    return this.serviceFactory.http2Service().post(this._initParams.initRetryGeoUrl);
  }

  private _getHistory(): any {
    return this._oAuth2Service().extractTokenProperty('historyData');
  }

  private _getB2C(): any {
    return this._oAuth2Service().extractTokenProperty('b2c');
  }

  public getCID(): string {
    // Get token from local storage.
    const accountData = this.getAccountData();
    return accountData && accountData.Cid;
  }

  public getTaxID(): string {
    const accountData = this.getAccountData();
    return accountData && accountData.TaxId;
  }

  public getUsername(): string {
    return this._oAuth2Service().extractTokenProperty('userName');
  }

  public getAccountData(): any {
    return this.serviceFactory.oAuth2Service().getAccountData();
  }

  public getB2CData(): any {
    const b2c = this._getB2C();

    if (!b2c) {
      return null;
    }

    const parseB2CData = JSON.parse(b2c);

    return parseB2CData.message;
  }

  public getHistoryToken(): any {
    const history = this._getHistory();

    if (!history) {
      return null;
    }
    return JSON.parse(history);
  }

  public getPlatformTokenFromLogin(): string {
    return this._extractTokenPropertyByName('login', 'Token');
  }

  public getUserFirstName(): string {
    return this._extractTokenPropertyByName('accountData', 'FirstName');
  }

  public getUserLastName(): string {
    return this._extractTokenPropertyByName('accountData', 'LastName');
  }

  public getPlatformToken(params?: object):Promise<ITokenResponse>{
    return this.serviceFactory.http2Service().post(this._initParams.tokenUrl, params)
      .then((response: ITokenResponse) => {
        return response;
      });
  }

  public renewLoginToken(params?: object): Promise<ITokenResponse> {
    return this.getPlatformToken(params).then( (response:ITokenResponse) => {
      this._oAuth2Service().updateTokenData('login', {Token: response.token});
      return response;
    })
  }

  public updateTokenData(propertyName: string, propertyValue: any): void {
    this._oAuth2Service().updateTokenData(propertyName, propertyValue);
  }

  private _extractTokenPropertyByName(properyToExtract: any, propertyToReturn: any): any {
    const loginData = this._oAuth2Service().extractTokenProperty(properyToExtract);

    if (!loginData) {
      return null;
    }

    const parseLoginData = JSON.parse(loginData);

    return parseLoginData[propertyToReturn];
  }

  public countPlayerChallenges(): number {
    const accountData = this.getAccountData();
    const numberOfChallenges = accountData && accountData.NumberOfChallenges;
    if(_.isNullOrUndefined(numberOfChallenges)) {
      return 0;
    }
    return numberOfChallenges;
  }

  public getRealityCheckData(): Promise<any> {
    if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
      return Promise.resolve();
    }

    return this.serviceFactory.http2Service().post(this._initParams.realityCheckDataUrl);
  }

  public async updateRealityCheckData(realityCheckIntevalMin: number): Promise<void> {
    return this.serviceFactory.regulationService().getRegulation().updateRealityCheckData(realityCheckIntevalMin);
  }

  public getUpdateRealityCheckDataUrl() {
    return this._initParams.updateRealityCheckDataUrl;
  }

  public getDepositWidgetEligibilityDetails(): ICashierWidgetsEligibilityDetails {
    if(this.serviceFactory.oAuth2Service().isAuthorized()) {
      return _.safeJSON(this.serviceFactory.oAuth2Service().extractTokenProperty('depositWidgetEligibilityDetails'), null);
    } else {
      return null;
    }
  }

  public getCashoutWidgetEligibilityDetails(): ICashierWidgetsEligibilityDetails {
    if(this.serviceFactory.oAuth2Service().isAuthorized()) {
      return _.safeJSON(this.serviceFactory.oAuth2Service().extractTokenProperty('cashoutWidgetEligibilityDetails'), null);
    } else {
      return null;
    }
  }

  public geoLocationFromLogin(): any {

    if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
      return false;
    }

    return _.safeJSON(this.serviceFactory.oAuth2Service().extractTokenProperty('geoLocation'), {});
  }

  public clearGeoLocationFromLogin(): void {
    this.updateTokenData('geoLocation', null);
  }

  public setFirebaseLoginToken(token: string): void{
    this._firebaseLoginToken = token;
  }

  public getFirebaseLoginToken(): string{
    return this._firebaseLoginToken;
  }
}

export default UserService;
